import React from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { toast } from 'sonner';
import { useMultiReports } from '../MultiReportsContext';
import { formatDate } from '../constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth header helper
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };
};

export function Header() {
  const { 
    selectedBookings,
    setShowBulkEditModal,
    filteredBookings,
    activeFieldTab,
    activeTab,
    activeFields,
    dateRange
  } = useMultiReports();

  // Export to Excel
  const handleExportExcel = async () => {
    try {
      toast.info('Generating Excel...');
      
      const exportData = filteredBookings.map(b => {
        const baseData = {
          booking_no: b.booking_no,
          customer_name: `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim(),
          mobile: b.mobile_no_1,
          vehicle: `${b.model || ''} ${b.variant || ''}`.trim(),
        };
        
        if (activeFieldTab === 'discounts') {
          baseData.chassis_no = b.chassis_no;
          baseData.registration_number = b.registration_number;
          baseData.dms_invoice_no = b.dms_invoice_no;
          baseData.dms_invoice_date = b.dms_invoice_date;
        }
        
        activeFields.forEach(field => {
          baseData[field.id] = b[field.id] || '';
        });
        
        return baseData;
      });

      const response = await axios.post(`${BACKEND_URL}/api/reports/multi-reports/export`, {
        data: exportData,
        tab: activeTab?.label || 'Report',
        fields: activeFields.map(f => ({ id: f.id, label: f.label })),
        isDiscountsTab: activeFieldTab === 'discounts',
        dateFrom: dateRange.from,
        dateTo: dateRange.to
      }, { 
        ...getAuthHeader(),
        responseType: 'blob' 
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `MultiReports_${activeTab?.label}_${dateRange.from}_to_${dateRange.to}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Excel downloaded');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  // Export to PDF
  const handleExportPDF = () => {
    try {
      toast.info('Generating PDF...');
      const doc = new jsPDF('l', 'mm', 'a4');
      
      doc.setFontSize(16);
      doc.text(`Multi Reports - ${activeTab?.label || 'Report'}`, 14, 15);
      doc.setFontSize(10);
      doc.text(`Date Range: ${formatDate(dateRange.from)} to ${formatDate(dateRange.to)}`, 14, 22);
      doc.text(`Total Records: ${filteredBookings.length}`, 14, 28);

      let headers = ['#', 'B.No', 'Customer', 'Mobile', 'Vehicle'];
      
      if (activeFieldTab === 'discounts') {
        headers = ['#', 'B.No', 'Chassis No', 'Customer', 'Mobile', 'Vehicle', 'Reg No', 'DMS Inv No', 'DMS Inv Date'];
      }
      
      activeFields.forEach(field => {
        headers.push(field.label);
      });

      const tableData = filteredBookings.slice(0, 200).map((b, idx) => {
        let row = [
          idx + 1,
          b.booking_no || '-',
        ];
        
        if (activeFieldTab === 'discounts') {
          row.push(b.chassis_no || '-');
        }
        
        row.push(
          `${b.customer_first_name || ''} ${b.customer_last_name || ''}`.trim() || '-',
          b.mobile_no_1 || '-',
          `${b.model || ''} ${b.variant || ''}`.trim() || '-'
        );
        
        if (activeFieldTab === 'discounts') {
          row.push(
            b.registration_number || '-',
            b.dms_invoice_no || '-',
            formatDate(b.dms_invoice_date)
          );
        }
        
        activeFields.forEach(field => {
          let value = b[field.id] || '-';
          if (field.type === 'number' && b[field.id]) {
            value = `Rs.${Number(b[field.id]).toLocaleString('en-IN')}`;
          } else if (field.type === 'date' && b[field.id]) {
            value = formatDate(b[field.id]);
          }
          row.push(value);
        });
        
        return row;
      });

      autoTable(doc, {
        startY: 35,
        head: [headers],
        body: tableData,
        styles: { fontSize: 7, cellPadding: 1.5 },
        headStyles: { fillColor: [59, 130, 246], fontSize: 7 },
        alternateRowStyles: { fillColor: [240, 249, 255] },
        margin: { left: 10, right: 10 }
      });

      doc.save(`MultiReports_${activeTab?.label}_${dateRange.from}_to_${dateRange.to}.pdf`);
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleBulkEditClick = () => {
    if (selectedBookings.length === 0) {
      toast.error('Please select at least one booking to bulk edit');
      return;
    }
    setShowBulkEditModal(true);
  };

  return (
    <div className="bg-white p-4 rounded-xl border shadow-sm">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Multi Reports</h2>
          <p className="text-sm text-gray-500">Edit multiple fields across bookings</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleExportExcel}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
            data-testid="export-excel-btn"
          >
            📊 Excel
          </button>
          <button
            onClick={handleExportPDF}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            data-testid="export-pdf-btn"
          >
            📄 PDF
          </button>
          <button
            onClick={handleBulkEditClick}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              selectedBookings.length > 0 
                ? 'bg-orange-600 text-white hover:bg-orange-700' 
                : 'bg-gray-300 text-gray-600 cursor-pointer'
            }`}
            data-testid="bulk-edit-btn"
          >
            🔄 Bulk Update {selectedBookings.length > 0 ? `(${selectedBookings.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}
